import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { datasourceBillsApi, type DatasourceBill} from '@/api/datasource-bills-api'
import type { APISession } from '@/types/session'

export type DatasourceSortField = 'bill_number' | 'title' | 'session' | null
export type DatasourceSortDirection = 'asc' | 'desc'

interface DatasourceBillsReturn {
  bills: DatasourceBill[]
  isLoading: boolean
  isError: boolean
  error: string | null
  selectedDataSource: string
  selectedSession: APISession | null
  setSelectedDataSource: (dataSource: string) => void
  setSelectedSession: (session: APISession) => void
  currentPage: number
  pageSize: number
  totalPages: number
  handlePageChange: (page: number) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  totalFilteredItems: number
  totalAllItems: number
  sortField: DatasourceSortField
  sortDirection: DatasourceSortDirection
  setSorting: (field: DatasourceSortField, direction: DatasourceSortDirection) => void
  refetch: () => void
}

export const useDatasourceBills = (
  initialDataSource: string = 'Maryland',
  initialSession?: APISession
): DatasourceBillsReturn => {
  const [selectedDataSource, setSelectedDataSource] = useState<string>(initialDataSource)
  const [selectedSession, setSelectedSession] = useState<APISession | null>(
    initialSession || '2024RS'
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortField, setSortField] = useState<DatasourceSortField>('bill_number')
  const [sortDirection, setSortDirection] = useState<DatasourceSortDirection>('asc')
  const pageSize = 50 // Larger page size for datasource view

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedDataSource, selectedSession, searchQuery])

  // Function to handle sorting
  const setSorting = useCallback((field: DatasourceSortField, direction: DatasourceSortDirection) => {
    setSortField(field)
    setSortDirection(direction)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Fetch datasource bills with pagination to get all bills
  const billsQuery = useQuery({
    queryKey: ['datasource-bills', selectedDataSource, selectedSession],
    queryFn: async () => {
      if (!selectedSession) {
        throw new Error('No session selected')
      }

      console.log('[useDatasourceBills] Fetching bills:', {
        data_source: selectedDataSource,
        session_code: selectedSession
      })

      // Fetch all bills by paginating through all pages
      let allBills: any[] = []
      let nextToken: string | undefined = undefined
      let pageCount = 0
      const maxPages = 50 // Safety limit to prevent infinite loops

      do {
        pageCount++
        console.log(`[useDatasourceBills] Fetching page ${pageCount}...`)
        
        const response = await datasourceBillsApi.listDatasourceBills({
          data_source: selectedDataSource,
          session_code: selectedSession,
          limit: 1000, // Maximum allowed by backend
          next_token: nextToken
        })

        allBills = [...allBills, ...response.items]
        nextToken = response.next_token
        
        console.log(`[useDatasourceBills] Page ${pageCount}: ${response.items.length} bills, total so far: ${allBills.length}`)
        
        // Safety break
        if (pageCount >= maxPages) {
          console.warn(`[useDatasourceBills] Reached maximum page limit (${maxPages}), stopping pagination`)
          break
        }
      } while (nextToken)

      console.log(`[useDatasourceBills] Fetched ${allBills.length} total bills across ${pageCount} pages`)
      
      return {
        items: allBills,
        next_token: undefined
      }
    },
    enabled: !!selectedSession && !!selectedDataSource,
    refetchOnWindowFocus: false,
    staleTime: 300000, // 5 minutes
    gcTime: 3600000 // 1 hour
  })

  // Get all bills from the query
  const allBills = useMemo(() => {
    return billsQuery.data?.items || []
  }, [billsQuery.data])

  // Filter bills based on search query
  const filteredBills = useMemo(() => {
    if (!searchQuery.trim()) return allBills

    const query = searchQuery.trim().toUpperCase()
    return allBills.filter(bill => {
      const billNumber = bill.bill_number || ''
      const title = bill.title || ''
      return billNumber.toUpperCase().includes(query) || 
             title.toUpperCase().includes(query)
    })
  }, [allBills, searchQuery])

  // Sort bills based on current sort field and direction
  const sortBills = useCallback((bills: DatasourceBill[]): DatasourceBill[] => {
    if (!sortField) return bills

    return [...bills].sort((a, b) => {
      let comparison = 0

      if (sortField === 'bill_number') {
        comparison = (a.bill_number || '').localeCompare(b.bill_number || '')
      } else if (sortField === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '')
      } else if (sortField === 'session') {
        comparison = (a.session || '').localeCompare(b.session || '')
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [sortField, sortDirection])

  // Apply sorting to filtered bills
  const sortedBills = useMemo(() => {
    return sortBills(filteredBills)
  }, [filteredBills, sortBills])

  // Get bills for the current page
  const getCurrentPageBills = (): DatasourceBill[] => {
    const startIdx = (currentPage - 1) * pageSize
    const endIdx = startIdx + pageSize
    return sortedBills.slice(startIdx, endIdx)
  }

  const totalItems = sortedBills.length
  const totalPages = Math.ceil(totalItems / pageSize)

  // Ensure current page is valid after filtering
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const billsToReturn = getCurrentPageBills()

  return {
    bills: billsToReturn,
    isLoading: billsQuery.isPending,
    isError: billsQuery.isError,
    error: billsQuery.isError ? 'Failed to load datasource bills' : null,
    selectedDataSource,
    selectedSession,
    setSelectedDataSource,
    setSelectedSession,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems: totalItems,
    totalAllItems: allBills.length,
    sortField,
    sortDirection,
    setSorting,
    refetch: billsQuery.refetch
  }
}
